import React, { useState, useMemo } from 'react';
import { Plus, Check, ArrowLeft, Mail, Target, Percent, Pencil, Trash2, Save } from 'lucide-react';
import { Avatar, Badge, Ring, Modal, fmtDate } from '../components/ui.jsx';
import { partnerStatusColor, stageColor, money, userById } from '../data.js';

function PartnerModal({ partner, onClose, onSave, team }) {
  const editing = Boolean(partner);
  const [done, setDone] = useState(false);
  const blankIfPlaceholder = (value) => (!value || value === '—' ? '' : value);
  const [form, setForm] = useState(partner ? { ...partner, contactName: blankIfPlaceholder(partner.contacts?.[0]?.name), contactRole: partner.contacts?.[0]?.role === 'Primary contact' ? '' : (partner.contacts?.[0]?.role || ''), contactEmail: blankIfPlaceholder(partner.contacts?.[0]?.email) } : {
    name: '', tier: 'Referral', commissionRate: '10', owner: team[0]?.id || '',
    contactName: '', contactRole: '', contactEmail: '', status: 'Onboarding', health: 50,
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (done) {
    return (
      <Modal title="" onClose={onClose}>
        <div className="modal-success">
          <span className="modal-success-icon"><Check size={24} /></span>
          <h2>{editing ? 'Partner updated' : 'Partner added'}</h2>
          <p>{form.name || 'New partner'} has been saved successfully.</p>
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      </Modal>
    );
  }
  return (
    <Modal
      title={editing ? 'Edit partner' : 'Add partner'}
      subtitle={editing ? 'Update partner identity, commercial terms, health and contact details.' : 'Bring a new partner into your channel program.'}
      onClose={onClose}
      actions={<>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" disabled={!form.name.trim()} onClick={() => { onSave(form); setDone(true); }}>{editing ? <><Save size={14}/> Save changes</> : 'Add partner'}</button>
      </>}
    >
      <label>Partner name<input placeholder="e.g. Meridian Consulting" value={form.name} onChange={set('name')} autoFocus /></label>
      <div className="modal-form-row">
        <label>Tier<select value={form.tier} onChange={set('tier')}><option>Strategic</option><option>Technology</option><option>Reseller</option><option>Referral</option></select></label>
        <label>Commission rate (%)<input value={form.commissionRate} onChange={set('commissionRate')} /></label>
      </div>
      <label>Partner manager (owner)<select value={form.owner} onChange={set('owner')}>{team.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select></label>
      <div className="modal-form-row"><label>Status<select value={form.status} onChange={set('status')}><option>Active</option><option>Onboarding</option><option>At risk</option></select></label><label>Relationship health ({form.health}%)<input type="range" min="0" max="100" value={form.health} onChange={set('health')} /></label></div>
      <div className="modal-form-row">
        <label>Primary contact<input placeholder="Full name" value={form.contactName} onChange={set('contactName')} /></label>
        <label>Contact role<input placeholder="e.g. Alliance Director" value={form.contactRole} onChange={set('contactRole')} /></label>
      </div>
      <label>Contact email<input placeholder="name@partner.com" value={form.contactEmail} onChange={set('contactEmail')} /></label>
    </Modal>
  );
}

function PartnerDetail({ partner, onBack, deals, onEdit, onDelete }) {
  const sourced = deals.filter((d) => d.source === partner.name);
  return (
    <div>
      <button className="record-back" onClick={onBack}><ArrowLeft size={15} /> Back to partners</button>
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: 16, justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <Avatar name={partner.name} color={partner.color} size="lg" />
            <div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{partner.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{partner.tier} partner · Partner since {partner.since}</div>
              <div style={{ marginTop: 8 }}><Badge tone={partnerStatusColor[partner.status]}>{partner.status}</Badge></div>
            </div>
          </div>
          <div className="partner-detail-actions"><button className="btn btn-ghost" onClick={() => onEdit(partner)}><Pencil size={14}/> Edit</button><button className="btn btn-danger" onClick={() => onDelete(partner)}><Trash2 size={14}/> Delete</button><Ring pct={partner.health} color={partner.health > 75 ? '#16A34A' : partner.health > 60 ? '#D97706' : '#DC2626'} /></div>
        </div>
      </div>

      <section className="detail-grid">
        <div className="card">
          <div className="section-title"><div><Target size={16} /><h3>Pipeline & revenue</h3></div></div>
          <div className="field-grid">
            <div><small>Open pipeline</small><b>{money(partner.pipeline)}</b></div>
            <div><small>Open deals</small><b>{partner.deals}</b></div>
            <div><small>Closed revenue (lifetime)</small><b>{money(partner.closedRevenue)}</b></div>
            <div><small>Commission rate</small><b><Percent size={12} />{partner.commissionRate}%</b></div>
          </div>
        </div>
        <div className="card">
          <div className="section-title"><div><Mail size={16} /><h3>Partner contact</h3></div></div>
          <div className="field-grid">
            {partner.contacts.map((c) => (
              <React.Fragment key={c.email}>
                <div><small>Name</small><b>{c.name}</b></div>
                <div><small>Role</small><b>{c.role}</b></div>
                <div style={{ gridColumn: '1 / -1' }}><small>Email</small><a><Mail size={13} />{c.email}</a></div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-header">
          <div><div className="card-title">Sourced deals</div><div className="card-subtitle">Opportunities registered by this partner</div></div>
        </div>
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>Company</th><th>Product</th><th>Stage</th><th>Value</th><th>Close date</th></tr></thead>
            <tbody>
              {sourced.map((d) => (
                <tr key={d.id}>
                  <td className="td-name">{d.account}</td>
                  <td>{(d.products || []).join(', ')}</td>
                  <td><Badge tone={stageColor[d.stage]}>{d.stage}</Badge></td>
                  <td className="td-mono">{money(d.value)}</td>
                  <td className="td-muted">{fmtDate(d.close)}</td>
                </tr>
              ))}
              {sourced.length === 0 && <tr><td colSpan={5} className="table-empty">No sourced deals yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}

export default function Partners({ partners = [], deals = [], team = [], onCreatePartner, onUpdatePartner, onDeletePartner }) {
  const [selected, setSelected] = useState(null);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const enriched = useMemo(() => partners.map((p) => {
    const sourced = deals.filter((d) => d.source === p.name);
    const open = sourced.filter((d) => !['Won', 'Lost'].includes(d.stage));
    return {
      ...p,
      deals: open.length,
      pipeline: open.reduce((s, d) => s + d.value, 0),
      closedRevenue: sourced.filter((d) => d.stage === 'Won').reduce((s, d) => s + d.value, 0),
    };
  }), [partners, deals]);

  const savePartner = async (form) => {
    if (editing) {
      const updated = await onUpdatePartner(editing, form);
      setSelected((current) => (current?.id === editing.id ? { ...current, ...updated } : current));
    } else {
      await onCreatePartner(form);
    }
    setEditing(null);
  };

  const deletePartner = async (partner) => {
    if (!window.confirm(`Delete ${partner.name}? This removes the partner profile and cannot be undone.`)) return;
    await onDeletePartner(partner);
    setSelected(null); setEditing(null);
  };

  if (selected) {
    const current = enriched.find((p) => p.id === selected.id) || selected;
    return <><PartnerDetail partner={current} onBack={() => setSelected(null)} deals={deals} onEdit={setEditing} onDelete={deletePartner} />{editing && <PartnerModal partner={editing} team={team} onClose={() => setEditing(null)} onSave={savePartner} />}</>;
  }

  return (
    <>
      <div className="page-head">
        <div className="page-head-left">
          <div className="page-title">Partners</div>
          <div className="page-subtitle">{partners.length} partners · Manage relationships, sourced pipeline and health.</div>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={() => setModal(true)}><Plus size={15} /> Add partner</button>
        </div>
      </div>

      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-card-value">{partners.length}</div>
          <div className="stat-card-label">Total partners</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-value">{money(enriched.reduce((s, p) => s + p.pipeline, 0))}</div>
          <div className="stat-card-label">Partner-sourced pipeline</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-value">{money(enriched.reduce((s, p) => s + p.closedRevenue, 0))}</div>
          <div className="stat-card-label">Lifetime closed revenue</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-value">{partners.filter((p) => p.status === 'At risk').length}</div>
          <div className="stat-card-label">Partners at risk</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
        {enriched.map((p) => (
          <div className="card" key={p.id} style={{ cursor: 'pointer' }} onClick={() => setSelected(p)}>
            <div className="card-body">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <Avatar name={p.name} color={p.color} />
                <div className="partner-card-actions"><Badge tone={partnerStatusColor[p.status]}>{p.status}</Badge><button className="btn-icon" title="Edit partner" onClick={(event) => { event.stopPropagation(); setEditing(p); }}><Pencil size={13}/></button><button className="btn-icon settings-delete" title="Delete partner" onClick={(event) => { event.stopPropagation(); deletePartner(p); }}><Trash2 size={13}/></button></div>
              </div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{p.name}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 12 }}>{p.tier} partner · {userById(team, p.owner)?.name || 'Unassigned'} · {p.commissionRate}% comm.</div>
              <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
                <div><div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>Open deals</div><b>{p.deals}</b></div>
                <div><div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>Pipeline</div><b>{money(p.pipeline)}</b></div>
              </div>
              <div className="progress-track"><div className="progress-bar" style={{ width: `${p.health}%`, background: p.color }} /></div>
              <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 4 }}>{p.health}% relationship health</div>
            </div>
          </div>
        ))}
      </div>

      {modal && <PartnerModal team={team} onClose={() => setModal(false)} onSave={savePartner} />}
      {editing && <PartnerModal partner={editing} team={team} onClose={() => setEditing(null)} onSave={savePartner} />}
    </>
  );
}
